const PROMOTIONS_ACTIVITY_TYPE = 60001

/**
 * 確認是否為 activity 類型（activityType 非 promotions）且 activityLocaleSettings 為非空陣列
 * @param {Object} activity - 活動物件
 * @returns {boolean} 是否需要使用 activityLocaleSettings
 */
function isActivityType(activity) {
  return (
    Number(activity?.activityType) !== PROMOTIONS_ACTIVITY_TYPE &&
    Array.isArray(activity?.activityLocaleSettings) &&
    activity.activityLocaleSettings.length > 0
  )
}

/**
 * 確認是否為 promotions 類型（activityType 為 promotions 或帶有 promotionsNo）且 localeSetting 為非空陣列
 * @param {Object} activity - 活動物件
 * @returns {boolean} 是否需要使用 localeSetting
 */
function isPromotionsType(activity) {
  return (
    (Number(activity?.activityType) === PROMOTIONS_ACTIVITY_TYPE || Boolean(activity?.promotionsNo)) &&
    Array.isArray(activity?.localeSetting) &&
    activity.localeSetting.length > 0
  )
}

/**
 * 確認是否需要過濾 localeSetting
 * 只要 activityLocaleSettings 或 localeSetting 任一為非空陣列即需要過濾
 * @param {Object} activity - 活動
 * @returns {boolean} 是否需要過濾
 */
function shouldFilterLocaleSetting(activity) {
  return isActivityType(activity) || isPromotionsType(activity)
}

/**
 * 過濾出對應語系的 localeSetting
 * @param {Array} localeSetting - 語系設定陣列
 * @param {string} locale - 當前語系
 * @param {string} defaultLocale - 預設語系
 * @returns {Object} 包含 currentLocaleData 和 defaultLocaleData
 */
function filterLocaleData(localeSetting, locale, defaultLocale) {
  const currentLocaleData = localeSetting?.find((set) => set.locale === locale) || {}
  const defaultLocaleData = localeSetting?.find((set) => set.locale === defaultLocale) || {}
  return { currentLocaleData, defaultLocaleData }
}

/**
 * 取得 url 資料
 * @param {Array} localeSetting - 語系設定陣列
 * @param {string} locale - 當前語系
 * @param {string} defaultLocale - 預設語系
 * @param {string} key - 資料鍵名
 * @returns {string|Array} key 為 'descriptionUrl' 時回傳陣列，其餘回傳第一筆 url 字串
 */
function getLocaleUrl(localeSetting, locale, defaultLocale, key) {
  const { currentLocaleData, defaultLocaleData } = filterLocaleData(localeSetting, locale, defaultLocale)
  const url = currentLocaleData?.[key]?.length > 0 ? currentLocaleData?.[key] : defaultLocaleData?.[key] || []

  if (url && Array.isArray(url) && url.length > 0) {
    if (key === 'descriptionUrl') {
      return url || []
    }
    return (url[0] || {}).url
  }
  return key === 'descriptionUrl' ? [] : ''
}

/**
 * 取得 text 資料
 * @param {Array} localeSetting - 語系設定陣列
 * @param {string} locale - 當前語系
 * @param {string} defaultLocale - 預設語系
 * @param {string} key - 資料鍵名
 * @returns {string} 文字內容
 */
function getLocaleText(localeSetting, locale, defaultLocale, key) {
  const { currentLocaleData, defaultLocaleData } = filterLocaleData(localeSetting, locale, defaultLocale)
  return currentLocaleData?.[key] || defaultLocaleData?.[key] || ''
}

/**
 * 過濾語系設定並取得對應資料（保留原有介面，用於 localeSetting 陣列）
 * @param {Array} localeSetting - 語系設定陣列
 * @param {string} locale - 當前語系
 * @param {string} defaultLocale - 預設語系
 * @param {string} key - 資料鍵名
 * @returns {string|Array} url 或文字內容
 */
function filterLocaleSetting(localeSetting, locale, defaultLocale, key) {
  const isUrl = String(key ?? '')
    .toUpperCase()
    .includes('URL')

  if (isUrl) {
    return getLocaleUrl(localeSetting, locale, defaultLocale, key)
  }
  return getLocaleText(localeSetting, locale, defaultLocale, key)
}

/**
 * 過濾 activity/promotion 物件並根據語言取得對應的資料
 * @param {Object} activity - 活動或優惠物件
 * @param {string} locale - 當前語言代碼 (例: 'zh-TW', 'en-US')
 * @param {string} defaultLocale - 預設語言代碼，當當前語言找不到資料時使用
 * @param {string} key - 要取得的資料 key (例: 'title', 'description', 'webCarouselUrl')
 * @returns {string|Array} 根據 key 的類型返回對應的資料
 */
function filterActivityLocaleSetting(activity, locale, defaultLocale, key) {
  // activityLocaleSettings 以 activityName 存放標題，與 localeSetting 的 title 不同
  const activityKey = key === 'title' ? 'activityName' : key

  // activityLocaleSettings 為非空陣列 → 使用 activityLocaleSettings 過濾
  if (isActivityType(activity)) {
    return filterLocaleSetting(activity.activityLocaleSettings, locale, defaultLocale, activityKey)
  }

  // localeSetting 為非空陣列 → 使用 localeSetting 過濾
  if (isPromotionsType(activity)) {
    return filterLocaleSetting(activity.localeSetting, locale, defaultLocale, key)
  }

  // 兩者皆為空陣列 → 直接回傳 activity 上對應 key 的值（key 'title' 同樣讀 activityName）
  // 舊圖片欄位為 [{ url }] 陣列，URL key 需取第一筆 url 字串（descriptionUrl 維持陣列），與 getLocaleUrl 回傳型別一致
  const rawValue = activity?.[activityKey] ?? ''
  const isUrlKey = String(key ?? '')
    .toUpperCase()
    .includes('URL')
  if (isUrlKey && key !== 'descriptionUrl' && Array.isArray(rawValue)) {
    return rawValue[0]?.url ?? ''
  }
  return rawValue
}

export { getLocaleUrl, getLocaleText, filterActivityLocaleSetting, shouldFilterLocaleSetting }
